import { StateMachineResponse } from '@wix/ambassador-iam-authentication-v1-authentication/types';
import { LoginEnvironment } from '../platform/environment';
import {
  buildLoginUrl,
  IdpCallbackData,
  idpLoginRequest,
  IdpLoginRequestOptions,
  idpLoginResultFromCallbackData,
  uint8ToBase64,
} from './openIdpPopup';

// Same-tab alternative to the IdP popup, using the popup's own IAM flow. IAM's
// SSO callback page is served from this origin, so in the same tab it shares
// sessionStorage with this page: when it finds `wix-idp-return-<sessionId>` it
// stores its callback data under `wix-idp-result-<sessionId>` and goes back to
// the return URL, instead of posting to a BroadcastChannel.

const PENDING_REDIRECT_KEY = 'wixIdpRedirect';
const returnKey = (sessionId: string) => `wix-idp-return-${sessionId}`;
const resultKey = (sessionId: string) => `wix-idp-result-${sessionId}`;

// The callback page goes straight back after storing its result, so the result
// is read on the very next page load. Older results were left by a return that
// never loaded this page, and belong to no current sign-in.
const RESULT_MAX_AGE_MS = 60_000;

interface HandedOverIdpResult extends IdpCallbackData {
  storedAt?: number;
}

interface PendingIdpRedirect {
  sessionId: string;
  idpConnectionId: string;
}

export type IdpRedirectReturn =
  | {
      status: 'completed';
      idpConnectionId: string;
      loginResponse: StateMachineResponse;
    }
  | { status: 'failed'; idpConnectionId: string; error: unknown }
  // Back on the page without a current result, e.g. the user went back from the IdP.
  | { status: 'abandoned'; idpConnectionId: string };

/**
 * Records the redirect in this tab and returns the IAM URL to navigate to, or
 * undefined when the tab cannot record it (storage or WebCrypto unavailable),
 * since the callback could then not hand the result back.
 */
export const prepareIdpRedirect = (
  env: LoginEnvironment,
  options: IdpLoginRequestOptions,
): string | undefined => {
  const crypto = env.global<Crypto>('crypto');
  if (typeof crypto?.randomUUID !== 'function') {
    return undefined;
  }
  const sessionId = crypto.randomUUID();
  // IAM requires a key for the popup mode it serves this flow with. The
  // handover does not use it: sessionStorage is already private to this tab.
  const encryptionKey = uint8ToBase64(
    crypto.getRandomValues(new Uint8Array(32)),
  );

  // Returning to the current URL keeps the post-auth params (postLogin,
  // redirectTo, …) that navigation reads after login.
  const returnUrl = new URL(env.url.href);
  returnUrl.hash = '';

  const pending: PendingIdpRedirect = {
    sessionId,
    idpConnectionId: options.idpConnectionId,
  };
  try {
    env.sessionStorage.setItem(PENDING_REDIRECT_KEY, JSON.stringify(pending));
    env.sessionStorage.setItem(
      returnKey(sessionId),
      JSON.stringify({ returnUrl: returnUrl.toString() }),
    );
  } catch {
    return undefined;
  }

  const loginUrl = buildLoginUrl({
    ...idpLoginRequest(options),
    sessionId,
    encryptionKey,
  });
  return new URL(loginUrl, env.url.origin).toString();
};

const takePendingRedirect = (
  env: LoginEnvironment,
): { pending: PendingIdpRedirect; rawResult: string | null } | undefined => {
  try {
    const pending: PendingIdpRedirect | null = JSON.parse(
      env.sessionStorage.getItem(PENDING_REDIRECT_KEY) ?? 'null',
    );
    if (!pending) {
      return undefined;
    }
    const rawResult = env.sessionStorage.getItem(resultKey(pending.sessionId));
    env.sessionStorage.removeItem(PENDING_REDIRECT_KEY);
    env.sessionStorage.removeItem(returnKey(pending.sessionId));
    env.sessionStorage.removeItem(resultKey(pending.sessionId));
    return { pending, rawResult };
  } catch {
    return undefined;
  }
};

/**
 * Reads and clears the result of a redirect this tab started, the same login
 * result or error the popup flow would have received. Undefined when no
 * redirect is pending.
 */
export const consumeIdpRedirectReturn = (
  env: LoginEnvironment,
): IdpRedirectReturn | undefined => {
  const taken = takePendingRedirect(env);
  if (!taken) {
    return undefined;
  }
  const { idpConnectionId } = taken.pending;
  if (!taken.rawResult) {
    return { status: 'abandoned', idpConnectionId };
  }
  try {
    const callbackData: HandedOverIdpResult = JSON.parse(taken.rawResult);
    if (
      typeof callbackData.storedAt !== 'number' ||
      Date.now() - callbackData.storedAt > RESULT_MAX_AGE_MS
    ) {
      return { status: 'abandoned', idpConnectionId };
    }
    return {
      status: 'completed',
      idpConnectionId,
      loginResponse: idpLoginResultFromCallbackData(callbackData),
    };
  } catch (error) {
    return { status: 'failed', idpConnectionId, error };
  }
};
